import { faPaperPlane } from "@fortawesome/free-regular-svg-icons";
import { faClose, faCompress, faExpand, faFloppyDisk, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../../../app/apiClient";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { decreaseFullScreenLoading, increaseFullScreenLoading } from "../../../appSlice";
import { currentToken } from "../../login/loginSlice";
import { activeComposingEmail, closeEmail, currentComposingEmail, refreshFolder } from "../emailSlice";

interface EmailFormState {
    from: string;
    to: string;
    cc: string;
    bcc: string;
    subject: string;
    body: string;
}

function emailFormReducer(state: EmailFormState, action: { field: keyof EmailFormState; value: string }): EmailFormState {
    return { ...state, [action.field]: action.value };
}

function initEmailForm(email: any): EmailFormState {
    return {
        from: email?.from || "",
        to: email?.to || "",
        cc: email?.cc || "",
        bcc: email?.bcc || "",
        subject: email?.subject || "",
        body: email?.body || ""
    };
}

// There's a single global "New Email" entry point regardless of how many
// compose windows are open, so it's a reliable place to return keyboard
// focus to when a compose window closes.
function returnFocusToComposeTrigger() {
    document.getElementById("compose-new-email-btn")?.focus();
}

function ComposeMailItem(props: any) {
    let userToken = useAppSelector(currentToken);
    let composingEmailGuid = useAppSelector(currentComposingEmail);
    let isActive = composingEmailGuid === props.email.guid;
    let dispatch = useAppDispatch();

    let [emailForm, dispatchEmailForm] = useReducer(emailFormReducer, props.email, initEmailForm);
    const setEmailField = (field: keyof EmailFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => dispatchEmailForm({ field, value: e.target.value });

    // Reply-All prefills a Cc, so surface the row immediately instead of hiding it behind the toggle.
    let [showCcBcc, setShowCcBcc] = useState(!!props.email.cc);
    let [isFullScreen, setIsFullScreen] = useState(false);
    // Mirrors Inbox.scss's $tablet breakpoint: below it, compose always fills the
    // viewport (no usable docked-popup size on a phone/tablet screen).
    let [isNarrowViewport, setIsNarrowViewport] = useState(() => window.matchMedia("(max-width: 600px)").matches);
    // The backend always writes a new S3 key on save/update (it deletes the old
    // object), so this must be replaced after every save or the next save/delete
    // would target an object that's already been removed.
    let [draftKey, setDraftKey] = useState<string | null>(props.email.draftKey || null);

    let fromInputRef = useRef<HTMLInputElement>(null);

    const sendEmailMessage = useCallback(() => {
        dispatch(increaseFullScreenLoading());
        let payload: any = {
            from: emailForm.from,
            to: emailForm.to,
            subject: emailForm.subject,
            text: emailForm.body,
            html: emailForm.body
        };
        if (emailForm.cc) {
            payload.cc = emailForm.cc;
        }
        if (emailForm.bcc) {
            payload.bcc = emailForm.bcc;
        }
        apiFetch("/api/emails/send", {
            method: "POST",
            token: userToken,
            body: JSON.stringify(payload)
        }).then((data) => {
            return data.json();
        }).then(() => {
            if (draftKey) {
                // Sent from an opened draft - remove it so it doesn't linger in Drafts.
                return apiFetch("/api/emails/draft/" + draftKey, {
                    method: "DELETE",
                    token: userToken
                }).then(() => dispatch(refreshFolder()));
            }
        }).then(() => {
            dispatch(closeEmail(props.email.guid));
            dispatch(decreaseFullScreenLoading());
            returnFocusToComposeTrigger();
        }).catch((error: any) => {
            dispatch(decreaseFullScreenLoading());
        });
    }, [dispatch, userToken, emailForm, draftKey, props.email.guid]);

    const saveDraftMessage = () => {
        dispatch(increaseFullScreenLoading());
        const path = draftKey ? "/api/emails/draft/" + draftKey : "/api/emails/draft";
        const method = draftKey ? "PUT" : "POST";
        apiFetch(path, {
            method,
            token: userToken,
            body: JSON.stringify({
                to: emailForm.to,
                cc: emailForm.cc,
                bcc: emailForm.bcc,
                subject: emailForm.subject,
                html: emailForm.body,
                text: emailForm.body
            })
        }).then((data) => {
            return data.json();
        }).then((data) => {
            setDraftKey(data.key);
            dispatch(refreshFolder());
            dispatch(decreaseFullScreenLoading());
        }).catch((error: any) => {
            dispatch(decreaseFullScreenLoading());
        });
    }

    const deleteDraftMessage = () => {
        if (!draftKey) {
            return;
        }
        dispatch(increaseFullScreenLoading());
        apiFetch("/api/emails/draft/" + draftKey, {
            method: "DELETE",
            token: userToken
        }).then(() => {
            dispatch(refreshFolder());
            dispatch(closeEmail(props.email.guid));
            dispatch(decreaseFullScreenLoading());
        }).catch((error: any) => {
            dispatch(decreaseFullScreenLoading());
        });
    }

    const toggleFullScreen = () => {
        if (!isActive) {
            dispatch(activeComposingEmail(props.email.guid));
        }
        setIsFullScreen(!isFullScreen);
    }

    useEffect(() => {
        if (!isActive) {
            return;
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (isFullScreen) {
                    setIsFullScreen(false);
                } else {
                    dispatch(closeEmail(props.email.guid));
                    returnFocusToComposeTrigger();
                }
            } else if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                sendEmailMessage();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isActive, isFullScreen, sendEmailMessage, dispatch, props.email.guid]);

    useEffect(() => {
        if (isActive) {
            fromInputRef.current?.focus();
        }
    }, [isActive]);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 600px)");
        const handleChange = (e: MediaQueryListEvent) => setIsNarrowViewport(e.matches);
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);

    let showFullScreen = isActive && (isFullScreen || isNarrowViewport);
    let emailTitle = emailForm.subject || props.emailHeader || "New Email";
    let fieldId = (field: string) => `compose-${props.email.guid}-${field}`;

    let composeWindow = (<div className={"newEmail" + (isActive ? " active" : "") + (showFullScreen ? " fullscreen" : "")} key={props.email.guid} role="dialog" aria-label={emailTitle}>
        <div className={"emailHeader"} onClick={() => dispatch(activeComposingEmail(props.email.guid))}>
            <div className={"iconsLeft"}>
                <button type="button" className={"iconBtn"} title="Close" aria-label="Close" onClick={(e) => { e.stopPropagation(); dispatch(closeEmail(props.email.guid)); returnFocusToComposeTrigger(); }}>
                    <FontAwesomeIcon icon={ faClose } />
                </button>
            </div>
            <button type="button" className={"emailHeaderToggle"} aria-label={`Expand ${emailTitle}`} onClick={(e) => { e.stopPropagation(); dispatch(activeComposingEmail(props.email.guid)); }}>
                <h1>{emailTitle}</h1>
            </button>
            <div className={"iconsRight"}>
                <button type="button" className={"iconBtn"} title="Save Draft" aria-label="Save Draft" onClick={(e) => { e.stopPropagation(); saveDraftMessage(); }}>
                    <FontAwesomeIcon icon={ faFloppyDisk } />
                </button>
                {draftKey && <button type="button" className={"iconBtn"} title="Delete Draft" aria-label="Delete Draft" onClick={(e) => { e.stopPropagation(); deleteDraftMessage(); }}>
                    <FontAwesomeIcon icon={ faTrash } />
                </button>}
                <button type="button" className={"iconBtn expandBtn"} title={isFullScreen ? "Exit full screen" : "Full screen"} aria-label={isFullScreen ? "Exit full screen" : "Full screen"} onClick={(e) => { e.stopPropagation(); toggleFullScreen(); }}>
                    <FontAwesomeIcon icon={ isFullScreen ? faCompress : faExpand } />
                </button>
                <button type="button" className={"sendBtn"} onClick={(e) => { e.stopPropagation(); sendEmailMessage(); }}>
                    <FontAwesomeIcon icon={ faPaperPlane } /> Send
                </button>
            </div>
        </div>
        <div className={"emailBody"}>
            <div className={"fieldRow"}>
                <label htmlFor={fieldId("from")} className={"fieldLabel"}>From</label>
                <input id={fieldId("from")} ref={fromInputRef} value={emailForm.from} placeholder="From Address" onChange={setEmailField("from")} autoCapitalize={"off"} />
            </div>
            <div className={"fieldRow"}>
                <label htmlFor={fieldId("to")} className={"fieldLabel"}>To</label>
                <input id={fieldId("to")} value={emailForm.to} placeholder="To Address" onChange={setEmailField("to")} autoCapitalize={"off"} />
                {!showCcBcc && <button type="button" className={"ccBccToggle"} onClick={() => setShowCcBcc(true)}>Cc Bcc</button>}
            </div>
            {showCcBcc && <div className={"fieldRow"}>
                <label htmlFor={fieldId("cc")} className={"fieldLabel"}>Cc</label>
                <input id={fieldId("cc")} value={emailForm.cc} placeholder="Cc Address" onChange={setEmailField("cc")} autoCapitalize={"off"} />
            </div>}
            {showCcBcc && <div className={"fieldRow"}>
                <label htmlFor={fieldId("bcc")} className={"fieldLabel"}>Bcc</label>
                <input id={fieldId("bcc")} value={emailForm.bcc} placeholder="Bcc Address" onChange={setEmailField("bcc")} autoCapitalize={"off"} />
            </div>}
            <div className={"fieldRow"}>
                <label htmlFor={fieldId("subject")} className={"fieldLabel"}>Subject</label>
                <input id={fieldId("subject")} value={emailForm.subject} placeholder="Subject" onChange={setEmailField("subject")} />
            </div>
            <textarea value={emailForm.body} placeholder="Write your message..." aria-label="Message body" onChange={setEmailField("body")}></textarea>
        </div>
    </div>);

    // Fullscreen is portaled straight to <body> so position:fixed is relative to the
    // viewport rather than .emailsBeingComposed, which is transformed for centering.
    return showFullScreen ? createPortal(composeWindow, document.body) : composeWindow;
}

export default ComposeMailItem;
